import React, { useMemo } from 'react';
import { View, ImageBackground, StyleSheet } from 'react-native';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { getCssEdgeFades, getCssRadialGlows, getGradientColors } from '@/constants/shared/theme';

interface GradientBackgroundProps {
  propertyImageUrl?: string | null;
  /**
   * Mobile web: fade the top and bottom edges into the page canvas color
   * (bg.base). Safari fills the strips behind its status bar and toolbar with
   * that one flat color and nothing else, so a full-page gradient or photo
   * meets them with a visible step unless its own edge rows are that color.
   * Set on the full-page background only; a panel reusing this component
   * never touches those strips.
   */
  blendEdges?: boolean;
  children: React.ReactNode;
}

/**
 * The two edge fades (see getCssEdgeFades), absolutely filling their parent.
 * Render it above the backdrop and before the content so the content paints
 * over it. Also used by the glass menu's full-screen backdrop, which redraws
 * the same gradient over the page while the menu is open.
 */
export const CanvasEdgeFade: React.FC = React.memo(() => {
  const { colors } = useThemeColors();
  const style = useMemo(
    () =>
      ({
        ...StyleSheet.absoluteFillObject,
        backgroundImage: getCssEdgeFades(colors.bg.base),
        pointerEvents: 'none',
      }) as any,
    [colors.bg.base],
  );
  return <View style={style} />;
});
CanvasEdgeFade.displayName = 'CanvasEdgeFade';

const GradientFallback: React.FC<{ blendEdges?: boolean; children: React.ReactNode }> = React.memo(
  ({ blendEdges, children }) => {
    const { mode } = useThemeColors();

    const gradientStyle = useMemo(() => {
      const cssGlows = getCssRadialGlows(mode);
      const { gradientStart, gradientEnd } = getGradientColors(mode);
      return {
        flex: 1,
        backgroundImage: `${cssGlows}, linear-gradient(180deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
      } as any;
    }, [mode]);

    return (
      <View style={gradientStyle}>
        {blendEdges ? <CanvasEdgeFade /> : null}
        {children}
      </View>
    );
  },
);

const ImageVariant: React.FC<{
  imageUrl: string;
  blendEdges?: boolean;
  children: React.ReactNode;
}> = React.memo(({ imageUrl, blendEdges, children }) => {
  const { colors } = useThemeColors();

  const imageOverlayStyle = useMemo(() => {
    const overlay = colors.gradient.imageOverlay;
    return {
      ...StyleSheet.absoluteFillObject,
      backgroundImage: `linear-gradient(180deg, ${overlay[0]} 0%, ${overlay[1]} 20%, ${overlay[2]} 40%, ${overlay[3]} 55%, ${overlay[4]} 70%, ${overlay[5]} 85%, ${overlay[6]} 100%)`,
    } as any;
  }, [colors.gradient.imageOverlay]);

  return (
    <View style={styles.container}>
      <ImageBackground
        source={{ uri: imageUrl }}
        style={StyleSheet.absoluteFill}
        resizeMode="cover"
      >
        <View style={imageOverlayStyle} />
      </ImageBackground>
      {blendEdges ? <CanvasEdgeFade /> : null}
      {children}
    </View>
  );
});

export const GradientBackground: React.FC<GradientBackgroundProps> = React.memo(
  ({ propertyImageUrl, blendEdges, children }) => {
    if (propertyImageUrl) {
      return (
        <ImageVariant imageUrl={propertyImageUrl} blendEdges={blendEdges}>
          {children}
        </ImageVariant>
      );
    }
    return <GradientFallback blendEdges={blendEdges}>{children}</GradientFallback>;
  },
);

GradientBackground.displayName = 'GradientBackground';

export const RadialGlowOverlay = React.memo(() => {
  const { mode } = useThemeColors();
  const glowStyle = useMemo(
    () => ({
      ...StyleSheet.absoluteFillObject,
      backgroundImage: getCssRadialGlows(mode),
      pointerEvents: 'none',
    }) as any,
    [mode],
  );
  return <View style={glowStyle} />;
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

